/* RolePreviewCard — one role preview rendered ONLY beneath its company card. */
import React from 'react';
import { Eye, Info } from 'lucide-react';
import { ROLE_PREVIEW_NOTICE, OfficialLink, Field, vehicleLabel, humanize, CoverImg } from './shared';

export function RolePreviewCard({ r }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4" data-testid={`bc-role-${r.rolePreviewId}`}>
      {r.imageUrl && (
        <CoverImg src={r.imageUrl} alt={r.imageAlt} className="rounded-xl overflow-hidden mb-3"
          testId={`bc-role-img-${r.rolePreviewId}${r.imageFallback ? '-fallback' : ''}`} />
      )}
      <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wider text-indigo-800 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full"
        data-testid={`bc-role-badge-${r.rolePreviewId}`}>
        <Eye size={10} /> {r.statusBadge}
      </span>
      <h4 className="font-extrabold text-gray-900 text-base leading-snug mt-2">{r.displayTitle}</h4>
      <p className="text-[11px] font-bold text-amber-700 uppercase tracking-widest mt-0.5">{r.roleFamily}</p>

      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <Field label="What this work usually involves">{r.whatThisWorkUsuallyInvolves}</Field>
        <Field label="Workplace focus">{r.workplaceFocus}</Field>
      </div>
      {r.skillsToExplore?.length > 0 && (
        <div className="mt-3">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-gray-500 mb-1">Skills to explore</p>
          <div className="flex flex-wrap gap-1.5">
            {r.skillsToExplore.map((s) => (
              <span key={s} className="text-[11px] font-semibold bg-teal-50 text-teal-800 border border-teal-200 px-2 py-0.5 rounded-full">{s}</span>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 grid sm:grid-cols-2 gap-3 rounded-xl bg-gray-50 border border-gray-100 p-3">
        <Field label="Vehicle / asset status to confirm" testId={`bc-role-vehicle-${r.rolePreviewId}`}>{vehicleLabel(r.vehicleOrAssetStatusToConfirm)}</Field>
        <Field label="Costs & setup to confirm" testId={`bc-role-costs-${r.rolePreviewId}`}>{humanize(r.costsAndSetupToConfirm)}</Field>
      </div>

      <p className="mt-3 text-xs text-amber-900 font-semibold flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded-xl p-2.5"
        data-testid={`bc-role-notice-${r.rolePreviewId}`}>
        <Info size={13} className="shrink-0 mt-0.5" /> {ROLE_PREVIEW_NOTICE}
      </p>
      {r.availabilityNotice && !/work preview, not a live opening/i.test(r.availabilityNotice) && (
        <p className="mt-1.5 text-[11px] text-gray-500 leading-relaxed">{r.availabilityNotice}</p>
      )}
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        {r.officialWorkerRoute?.checkedAt && (
          <span className="text-[10px] text-gray-400 font-semibold">Official route checked {r.officialWorkerRoute.checkedAt}</span>
        )}
        <OfficialLink href={r.officialWorkerRoute?.url} label="Open official route and confirm role + city" small
          testId={`bc-role-cta-${r.rolePreviewId}`} />
      </div>
    </div>
  );
}
